import * as React from 'react';
import DeltaResult, { EditorModel, ViewType } from '../DeltaResult';
import FormDataType from './ConfigFormData';
import FileTabs from './FileTabs';
import { Operations } from './NavigatorData';
import { OperationContext } from './OperationContext';

export enum TabGroup {
  group1,
  group2
}

export interface IFilesContentProps {
  code?: string;
  deltaObj?: any;
}

export enum FileTabNames {
  inputATab = 0,
  inputBTab = 1,
  rawOutTab = 2,
  diffOutTab = 3,
  ancestorInputTab = 4
}

export interface FilesContentProps {
  tabGroups: TabGroupData;
  onUnmount: (model: EditorModel, editorId: FileTabNames) => void;
  editorModels: (EditorModel | null)[];
  onTextChange: (editorId: FileTabNames, text: string) => void;
  onDidGetFocus: (editorId: FileTabNames) => void;
  onFormChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFormDrop: (event: React.DragEvent<HTMLInputElement>, InputFileName: string) => void;
  formValues: FormDataType;
  monacoTheme: string;
}

export type TabGroupData = {
  tabGroup1: FileData[];
  tabGroup2: FileData[];
  activeTabGroup: TabGroup;
  tabValue1: number;
  tabValue2: number;
  onTabChange1: (event: React.ChangeEvent<{}>, newValue: number, operationType: Operations) => void;
  onTabChange2: (event: React.ChangeEvent<{}>, newValue: number, operationType: Operations) => void;
}

export interface FileData {
  tabLabel: string,
  tabSuffix: TabSuffix,
  tabInfo: string,
  fileContent: string,
  viewType: ViewType,
  hideTab?: boolean
}

export enum TabSuffix {
  None,
  Raw,
  Diff,
  Graft,
  Patch,
  ThreeWayMerge,
  TwoWayMerge
}

function FilesContent(props: FilesContentProps) {
  const { tabGroups } = props;
  const { tabGroup1, tabGroup2, activeTabGroup, tabValue1, tabValue2, onTabChange1, onTabChange2 } = tabGroups;
  const { onUnmount, onTextChange, onDidGetFocus, editorModels, monacoTheme } = props;

  const opContextType = React.useContext(OperationContext).operationType;

  const createEditor = (tabKey: FileTabNames, tabPositionForEditor: number, tabGroupId: TabGroup, tabGroupData: FileData[], activeTabPosition: number) => {
    const tabDataForEditor = tabGroupData[tabPositionForEditor];
    const {fileContent, viewType, tabInfo} = tabDataForEditor;
    return (
    <DeltaResult
      key={tabKey}
      onUnmount={onUnmount}
      onTextChange={onTextChange}
      editorId={tabKey}
      code={fileContent}
      viewType={viewType}
      info={tabInfo}
      display={activeTabPosition === tabPositionForEditor}
      inActiveGroup={activeTabGroup === tabGroupId}
      onDidGetFocus={onDidGetFocus}
      editorModel={editorModels[tabKey]} 
      monacoTheme={monacoTheme}/>
    )
  }

  let editorsInTab1;
  let editorsInTab2;

  switch (opContextType) {
    case Operations.threeWayMerge:
      const editorAncestor = createEditor(FileTabNames.ancestorInputTab, 0, TabGroup.group1, tabGroup1, tabValue1);
      const editorInputA = createEditor(FileTabNames.inputATab, 1, TabGroup.group1, tabGroup1, tabValue1);
      editorsInTab1 = [editorAncestor, editorInputA];
      if (tabGroup1.length > 2) {
        const editorRawOut = createEditor(FileTabNames.rawOutTab, 2, TabGroup.group1, tabGroup1, tabValue1);
        editorsInTab1.push(editorRawOut);
      }
      const editorInputB = createEditor(FileTabNames.inputBTab, 0, TabGroup.group2, tabGroup2, tabValue2);
      editorsInTab2 = [editorInputB];
      if (tabGroup2.length === 2) {
        const editorDiffOut = createEditor(FileTabNames.diffOutTab, 1, TabGroup.group2, tabGroup2, tabValue2);
        editorsInTab2.push(editorDiffOut);
      }
      break;
    default:
      {
        const editorInputA = createEditor(FileTabNames.inputATab, 0, TabGroup.group1, tabGroup1, tabValue1);
        editorsInTab1 = [editorInputA];
        if (tabGroup1.length > 1) {
          const editorRawOut = createEditor(FileTabNames.rawOutTab, 1, TabGroup.group1, tabGroup1, tabValue1);
          editorsInTab1.push(editorRawOut);
        }
        const editorInputB = createEditor(FileTabNames.inputBTab, 0, TabGroup.group2, tabGroup2, tabValue2);
        editorsInTab2 = [editorInputB];
        if (tabGroup2.length === 2) {
          const editorDiffOut = createEditor(FileTabNames.diffOutTab, 1, TabGroup.group2, tabGroup2, tabValue2);
          editorsInTab2.push(editorDiffOut);
        }
      }
      break;
  }

  return (
    <div className='editorsContainer'>
      <div className="centerPanel">
        <div className="flexContainer">
          <div className="flex1">
            <FileTabs
              handleTabChange={onTabChange1}
              tabValue={tabValue1}
              tabGroupData={tabGroup1} />
          </div>
          <div className="editorsBlock">
              {editorsInTab1}
          </div>
        </div>
      </div>
      <div className="rightPanel">
        <div className="flexContainer">
          <div className="flex1">
            <FileTabs
              handleTabChange={onTabChange2}
              tabValue={tabValue2}
              tabGroupData={tabGroup2} />
          </div>
          <div className="editorsBlock">
              {editorsInTab2}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilesContent;